/**
 * A verification program (job data.program): the frozen cohort, evidence coverage, packets sent,
 * and the provider's decision. Until an issuer decides, claims are shown as self-reported.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import type { JobDetail, JobItemDTO } from "../../../contract";
import { api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { SchemaForm } from "../../../web/forms/SchemaForm";
import { useLabels } from "../../../web/forms/pickers";
import { registry } from "../../../web/registry";
import { Button, Card, Checkbox, Chip, ErrorNote, Field, Loading, Section, Segmented, Sheet, TagLabel, TextInput, toast } from "../../../web/ui/kit";
import type { EvidencePacket, ExchangeDTO, ProgramDecision } from "../shared/types";
import { EXCHANGE_STATUS, StatusChip } from "./labels";
import { EvidenceView, PrepareSheet } from "./Workspace";

interface Overview { job: JobDetail; packet: EvidencePacket; exchanges: ExchangeDTO[]; decision: ProgramDecision | null }

const DECISION: Record<ProgramDecision["status"], string> = { enrolled: "Enrolled", partial: "Partly enrolled", rejected: "Not accepted", expired: "Expired" };

export function ProgramJob({ job, items, refresh }: { job: JobDetail; items: JobItemDTO[]; refresh(): void }) {
  useLanguage();
  const can = useCan();
  const navigate = useNavigate();
  const q = useApi<Overview>(`/m/data/programs/${job.id}`);
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const provider = job.data.provider_id as string | undefined;
  const labels = useLabels(provider ? [{ kind: "contact", id: provider }] : []);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const { packet, exchanges, decision } = q.data;
  const reload = () => { void q.refetch(); refresh(); };
  return (
    <div className="stack">
      <Card>
        <h3>{String(job.data.program ?? job.title)}</h3>
        <p className="muted">{[provider ? t("Provider: {{name}}", { name: labels[provider] ?? "" }) : t("No provider picked yet"), job.data.version ? String(job.data.version) : null].filter(Boolean).join(" · ")}</p>
        {job.data.claims ? <p>{String(job.data.claims)}</p> : null}
        {decision ? (
          <p><Chip tone={decision.status === "enrolled" ? "good" : decision.status === "partial" ? "warn" : "bad"} icon="shield">{t(DECISION[decision.status])}</Chip>{" "}
            {t("Decided by {{issuer}} on {{date}}", { issuer: decision.issuer, date: formatDate(decision.decided_on) })}{decision.valid_until ? ` · ${t("valid until {{date}}", { date: formatDate(decision.valid_until) })}` : ""}{decision.reference ? ` · ${decision.reference}` : ""}</p>
        ) : <p><Chip tone="neutral">{t("Self-reported")}</Chip> {t("These are your records. Only the provider can verify them.")}</p>}
        {can("record") && <div className="row-actions"><Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(true)}>{t("Program details")}</Button></div>}
      </Card>
      {can("data") && (
        <div className="data-actions-grid">
          <Button size="lg" variant={exchanges.length ? "secondary" : "primary"} icon="send" onClick={() => setPreparing(true)}>{t("Prepare the evidence packet")}</Button>
          <Button size="lg" variant={exchanges.length && !decision ? "primary" : "secondary"} icon="award" onClick={() => setDeciding(true)}>{decision ? t("Change the decision") : t("Record the provider's decision")}</Button>
        </div>
      )}
      {exchanges.length > 0 && (
        <Section title={t("Packets sent")}>
          <ul className="data-files">{exchanges.map((e) => (
            <li key={e.id} className="data-file">
              <span className="data-file-name">{e.title}<small>{[formatDate(e.created_at), plural(e.counts.total, "{{count}} animal", "{{count}} animals")].join(" · ")}</small></span>
              <StatusChip map={EXCHANGE_STATUS} value={e.status} />
              <Button size="sm" variant="ghost" onClick={() => navigate(`/work/data/exchanges/${e.id}`)}>{t("Open")}</Button>
            </li>
          ))}</ul>
        </Section>
      )}
      <Section title={t("Evidence")} hint={t("Gaps don't stop you from preparing a draft packet; the provider decides what they accept.")}>
        <EvidenceView packet={packet} />
      </Section>
      {editing && <DetailsSheet job={job} onClose={() => setEditing(false)} onSaved={reload} />}
      {deciding && <DecisionSheet job={job} items={items} packet={packet} decision={decision} providerName={provider ? labels[provider] : undefined} onClose={() => setDeciding(false)} onSaved={reload} />}
      {preparing && <PrepareSheet onClose={() => setPreparing(false)} onDone={(id) => navigate(`/work/data/exchanges/${id}`)}
        initial={{ adapter: "data.evidence_csv", contact_id: provider ?? null, job_id: job.id, animal_ids: items.filter((i) => i.subject_kind === "animal").map((i) => i.subject_id), title: job.title }} />}
    </div>
  );
}

function DetailsSheet({ job, onClose, onSaved }: { job: JobDetail; onClose: () => void; onSaved: () => void }) {
  const def = registry.jobs.get("data.program")!;
  const [value, setValue] = useState<Record<string, unknown>>({ ...job.data });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const data = Object.fromEntries(Object.keys(def.fields ?? {}).map((k) => [k, value[k] ?? null]));
      await api.patch(`/jobs/${job.id}`, { data });
      toast(t("Saved"), { tone: "good" });
      onSaved();
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Program details")} footer={<Button variant="primary" size="lg" block busy={busy} onClick={() => void save()}>{t("Save")}</Button>}>
      <SchemaForm fields={def.fields ?? {}} value={value} onChange={setValue} />
    </Sheet>
  );
}

function DecisionSheet({ job, items, packet, decision, providerName, onClose, onSaved }: { job: JobDetail; items: JobItemDTO[]; packet: EvidencePacket; decision: ProgramDecision | null; providerName?: string; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState<ProgramDecision["status"]>(decision?.status ?? "enrolled");
  const [issuer, setIssuer] = useState(decision?.issuer ?? providerName ?? "");
  const [reference, setReference] = useState(decision?.reference ?? "");
  const [on, setOn] = useState(decision?.decided_on ?? new Date().toISOString().slice(0, 10));
  const [until, setUntil] = useState(decision?.valid_until ?? "");
  const [rejected, setRejected] = useState<Record<string, string>>(Object.fromEntries((decision?.rejected ?? []).map((r) => [r.id, r.reason ?? ""])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const labels = new Map(packet.animals.map((a) => [a.id, a.tag ?? a.label]));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/m/data/programs/${job.id}/decision`, { status, issuer, reference: reference || undefined, decided_on: on, valid_until: until || undefined, rejected: status === "partial" ? Object.entries(rejected).map(([id, reason]) => ({ id, reason: reason || undefined })) : [] });
      await invalidateAll();
      toast(t("Decision saved"), { tone: "good" });
      onSaved();
      onClose();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("The provider's decision")} footer={<Button variant="primary" size="lg" block busy={busy} disabled={!issuer.trim() || !on} onClick={() => void save()}>{t("Save the decision")}</Button>}>
      <Field label={t("What they decided")}>
        <Segmented label={t("What they decided")} value={status} onChange={(v) => setStatus(v)} options={(Object.keys(DECISION) as ProgramDecision["status"][]).map((k) => ({ value: k, label: t(DECISION[k]) }))} />
      </Field>
      <Field label={t("Decided by")} hint={t("A decision needs the issuer's name to show as verified.")}><TextInput value={issuer} onChange={(e) => setIssuer(e.target.value)} /></Field>
      <Field label={t("Their reference")} optional><TextInput value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      <Field label={t("Decided on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
      <Field label={t("Good until")} optional><TextInput type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></Field>
      {status === "partial" && (
        <Field label={t("Which animals weren't accepted")}>
          <ul className="data-files">{items.filter((i) => i.subject_kind === "animal").map((i) => (
            <li key={i.id} className="data-file">
              <Checkbox checked={i.subject_id in rejected} onChange={(v) => setRejected((r) => { const n = { ...r }; if (v) n[i.subject_id] = ""; else delete n[i.subject_id]; return n; })} label={labels.get(i.subject_id) ?? i.label} />
              <span className="data-file-name"><TagLabel tag={labels.get(i.subject_id) ?? i.label} /></span>
              {i.subject_id in rejected && <TextInput value={rejected[i.subject_id]} placeholder={t("Why")} aria-label={t("Why")} onChange={(e) => setRejected((r) => ({ ...r, [i.subject_id]: e.target.value }))} />}
            </li>
          ))}</ul>
        </Field>
      )}
      {error ? <ErrorNote error={error} /> : null}
    </Sheet>
  );
}
